import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEdit, faSync, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { organizationList } from "../../api/api";
import { getProviderSettingsByOrg, loadProviderSettings, saveProviderSettings } from "../../api/adminApi";

const NotificationProviderSettings = () => {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [orgList, setOrgList] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const [settings, setSettings] = useState([]);


  const [settingsData, setSettingsData] = useState({
    name: "",
    displayName: "",
    apiKey: "",
    apiUrl: "",
    apiSecret: "",
    active: false,
    organizationId: 0
  });

  useEffect(() => {
    fetchOrganizationList();
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      setLoading(true);
      const response = await loadProviderSettings();
      if (response.status === 200) {
        setSettings(response.data);
      } else {
        console.error("Failed to fetch organization settings:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching organization settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationList = async () => {
    try {
      setLoading(true); // Indicate loading state
      const response = await organizationList();
      if (response.status === 200) {
        setOrgList(response.data);
      } else {
        console.error("Failed to fetch organization list:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching organization list:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle dropdown selection
  const handleOrgSelect = (e) => {
    const orgId = parseInt(e.target.value, 10);
    if (orgId) {
      setSelectedOrg(orgId);
    } else {
      setSelectedOrg(0);
      resetForm();
    }
  };

  // Reset the form to default values
  const resetForm = () => {
    setSettingsData({
      name: "",
      displayName: "",
      apiKey: "",
      apiUrl: "",
      apiSecret: "",
      active: false,
      organizationId: 0,
    });
  };

  // Handle input changes in the form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettingsData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    const payload = {
      ...settingsData,
      organizationId: selectedOrg || settingsData.organizationId,
    };
    try {
      await saveProviderSettings(payload);
      alert("Settings saved successfully.");
    } catch (error) {
      alert("Failed to save settings. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const getStatus = (status) => {
    console.log('------------------------------', status);
    return status == 1 ? 'Active' : 'Inactive';
  };

  return (
    <>
      <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg">
        <h1 className="text-2xl font-semibold text-gray-800 mb-6">Notification Provider Settings</h1>

        {isLoading ? (
          <p className="text-gray-500">Loading Organization...</p>
        ) : (
          <>
            {/* Dropdown for selecting an organization */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Organization</label>
              <select
                onChange={handleOrgSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">-- Select an organization --</option>
                {orgList.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Form fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  value={settingsData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
                <input
                  type="text"
                  name="displayName"
                  value={settingsData.displayName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                <input
                  type="text"
                  name="apiKey"
                  value={settingsData.apiKey}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">API URL</label>
                <input
                  type="text"
                  name="apiUrl"
                  value={settingsData.apiUrl}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">API Secret</label>
                <input
                  type="text"
                  name="apiSecret"
                  value={settingsData.apiSecret}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Is Active</label>
                <input
                  type="checkbox"
                  name="active"
                  checked={settingsData.active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="mt-6 w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {isLoading ? "Saving..." : "Save Settings"}
            </button>
          </>
        )}
      </div>
      <br />
      <div className="bg-white p-8">
        {isLoading ? (
          <p className="text-gray-500">Loading Provider Settings...</p>
        ) : (
          <>
            <div className="space-y-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-orange-600">Setting List</h2>
                <button className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600" onClick={() => navigate('/settings/new-org')}>
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
              <div className="bg-blue-50 shadow-lg rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Organization</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Display Name</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">API Url</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">API Key</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">API Secret</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase">Is Active</th>
                      <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {settings.map((org) => (
                      <tr key={org.id}>
                        <td className="px-6 py-4 text-sm text-gray-900">{org.organizationName}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{org.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{org.displayName}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{org.apiUrl}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{org.apiKey}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{org.apiSecret}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{getStatus(org.active)}</td>
                        <td className="px-6 py-4 text-right text-sm space-x-2">
                          <button className="text-blue-600 hover:underline"><FontAwesomeIcon icon={faEye} /></button>
                          <button className="text-green-600 hover:underline"><FontAwesomeIcon icon={faEdit} /></button>
                          <button className="text-yellow-600 hover:underline"><FontAwesomeIcon icon={faSync} /></button>
                          <button className="text-red-600 hover:underline"><FontAwesomeIcon icon={faTrash} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
export default NotificationProviderSettings;
